import React, { useCallback, useEffect, useRef } from 'react';
import { DialogContent } from '@/components/ui/dialog';
import useVisualViewport from '@/hooks/useVisualViewport';
import { keepFocusedFieldVisible, shouldCenterFocusedField } from '@/lib/focusVisibility';
import useBottomSheetViewport from '@/hooks/useBottomSheetViewport';

// Opt-in: keep the shared Radix Dialog unchanged for the rest of Klaro.
export default function ViewportDialogContent({ active, style, onFocusCapture, children, ...props }) {
  const viewport = useVisualViewport(active);
  const bottomSheetViewport = useBottomSheetViewport(active);
  const contentRef = useRef(null);
  const focusVisibilityTimerRef = useRef(null);
  const ensureFocusedVisible = useCallback(() => {
    keepFocusedFieldVisible({ root: contentRef.current, viewport, center: shouldCenterFocusedField(viewport) });
  }, [viewport]);

  const scheduleFocusedFieldCheck = useCallback(() => {
    window.requestAnimationFrame(ensureFocusedVisible);
    window.clearTimeout(focusVisibilityTimerRef.current);
    focusVisibilityTimerRef.current = window.setTimeout(() => {
      window.requestAnimationFrame(ensureFocusedVisible);
    }, 250);
  }, [ensureFocusedVisible]);

  useEffect(() => {
    if (active) scheduleFocusedFieldCheck();
    else window.clearTimeout(focusVisibilityTimerRef.current);
    return () => window.clearTimeout(focusVisibilityTimerRef.current);
  }, [active, viewport.height, viewport.top, viewport.bottomInset, scheduleFocusedFieldCheck]);

  return <DialogContent
    {...props}
    ref={contentRef}
    style={{
      ...style,
      top: bottomSheetViewport ? 'auto' : `${viewport.top + viewport.height / 2}px`,
      bottom: bottomSheetViewport ? `${viewport.bottomInset}px` : 'auto',
      height: bottomSheetViewport ? 'auto' : style?.height,
      maxHeight: `${Math.max(0, viewport.height - 16)}px`,
      transform: bottomSheetViewport ? 'translateX(-50%)' : 'translate(-50%, -50%)',
      borderRadius: bottomSheetViewport ? '1.5rem 1.5rem 0 0' : style?.borderRadius,
      overflowY: 'auto',
      padding: bottomSheetViewport ? '1rem' : style?.padding,
      paddingBottom: bottomSheetViewport ? 'max(1rem, env(safe-area-inset-bottom))' : style?.paddingBottom,
    }}
    data-receipt-bottom-sheet={bottomSheetViewport ? 'true' : undefined}
    className={`${props.className || ''} ${bottomSheetViewport ? '!w-full !max-w-none !gap-3' : ''}`}
    onFocusCapture={event => {
      onFocusCapture?.(event);
      scheduleFocusedFieldCheck();
    }}
  >{bottomSheetViewport && <div className="mx-auto h-1 w-10 shrink-0 rounded-full bg-slate-300" aria-hidden="true"/>}{children}</DialogContent>;
}
